/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Briefcase,
  Building2,
  Calendar,
  MessageSquare,
  Eye,
  TrendingUp,
  Sparkles,
  Plus,
  ArrowUpRight,
  Zap,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import axiosClient from '../api/axiosClient';

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axiosClient.get('/analytics/dashboard');
        setStats(res.data);
      } catch (err) {
        // Handled silently
      }
    };
    fetchStats();
  }, []);

  const statCards = [
    { title: 'Total Pages', value: stats?.counts?.pages || 5, icon: FileText, change: '+2 new', color: 'from-blue-600 to-indigo-600' },
    { title: 'Active Services', value: stats?.counts?.services || 11, icon: Briefcase, change: '100% SLA', color: 'from-purple-600 to-pink-600' },
    { title: 'Industries Served', value: stats?.counts?.industries || 14, icon: Building2, change: 'Multi-sector', color: 'from-emerald-600 to-teal-600' },
    { title: 'Bookings', value: stats?.counts?.bookings || 8, icon: Calendar, change: `${stats?.counts?.pendingBookings || 2} Pending`, color: 'from-amber-600 to-orange-600' },
    { title: 'Contact Messages', value: stats?.counts?.messages || 12, icon: MessageSquare, change: `${stats?.counts?.unreadMessages || 3} Unread`, color: 'from-cyan-600 to-blue-600' },
    { title: 'Unique Visitors', value: (stats?.counts?.visitors || 1240).toLocaleString(), icon: Eye, change: '+18.4% this month', color: 'from-indigo-600 to-purple-600' },
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Welcome & Quick Action Banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-blue-950 via-slate-900 to-indigo-950 border border-slate-800 p-8 shadow-2xl">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wider">
              <Zap size={14} />
              <span>FAJTech Enterprise Admin v2.0</span>
            </div>
            <h1 className="text-3xl font-black text-white tracking-tight">System Operational Overview</h1>
            <p className="text-sm text-slate-300">
              Manage website content, review incoming bookings, track visitor analytics, and utilize AI assistance.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/admin/pages"
              className="px-4 py-2.5 rounded-xl font-bold text-xs bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 flex items-center space-x-2 transition-all"
            >
              <Plus size={16} />
              <span>Edit Home Page</span>
            </Link>
            <Link
              to="/admin/ai-assistant"
              className="px-4 py-2.5 rounded-xl font-bold text-xs bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 flex items-center space-x-2 transition-all"
            >
              <Sparkles size={16} />
              <span>AI Copilot</span>
            </Link>
          </div>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <div
              key={idx}
              className="bg-slate-900 border border-slate-800/80 hover:border-slate-700 rounded-2xl p-6 shadow-xl transition-all duration-200 hover:-translate-y-1"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{card.title}</span>
                <div className={`p-2.5 rounded-xl bg-gradient-to-tr ${card.color} text-white shadow-md`}>
                  <Icon size={20} />
                </div>
              </div>
              <div className="mt-4 flex items-baseline justify-between">
                <span className="text-3xl font-extrabold text-white">{card.value}</span>
                <span className="text-xs font-semibold text-emerald-400 flex items-center space-x-1">
                  <TrendingUp size={12} />
                  <span>{card.change}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Visitor Traffic Area Chart */}
        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-base text-white">Weekly Traffic & Booking Trends</h3>
              <p className="text-xs text-slate-400">Total website visitors and conversion count per day</p>
            </div>
            <span className="text-xs font-semibold text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-full">
              Live Data
            </span>
          </div>

          <div className="h-72 w-full pt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats?.trafficTrends || []}>
                <defs>
                  <linearGradient id="visitorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#64748b" fontSize={12} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={12} tickLine={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0f172a', borderColor: '#1e293b', borderRadius: '12px', color: '#fff' }}
                />
                <Area type="monotone" dataKey="visitors" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#visitorGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Visited Pages & Devices */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-6 shadow-xl">
          <div>
            <h3 className="font-bold text-base text-white">Top Visited Pages</h3>
            <p className="text-xs text-slate-400">Most requested routes by users</p>
          </div>

          <div className="space-y-4">
            {(stats?.topPages || []).map((page, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
                <span className="text-xs font-mono text-blue-400 font-semibold truncate max-w-[160px]">{page.path}</span>
                <span className="text-xs font-bold text-white bg-slate-800 px-2.5 py-1 rounded-lg">
                  {page.views} views
                </span>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-slate-800">
            <Link
              to="/admin/analytics"
              className="text-xs font-bold text-blue-400 hover:text-blue-300 flex items-center justify-between group"
            >
              <span>View Full Analytics Dashboard</span>
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Action Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { title: 'Page Builder', desc: 'Reorder homepage sections', link: '/admin/pages', icon: FileText, color: 'text-blue-400' },
          { title: 'Manage Services', desc: 'Add or update IT services', link: '/admin/services', icon: Briefcase, color: 'text-purple-400' },
          { title: 'Media Library', desc: 'Upload images & files', link: '/admin/media', icon: Sparkles, color: 'text-emerald-400' },
          { title: 'Bookings Queue', desc: 'Review consultation requests', link: '/admin/bookings', icon: Calendar, color: 'text-amber-400' },
        ].map((action, i) => {
          const Icon = action.icon;
          return (
            <Link
              key={i}
              to={action.link}
              className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-blue-500/50 transition-all flex flex-col justify-between group"
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className={`w-6 h-6 ${action.color}`} />
                <ArrowUpRight size={18} className="text-slate-500 group-hover:text-white transition-colors" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-white group-hover:text-blue-400 transition-colors">{action.title}</h4>
                <p className="text-xs text-slate-400 mt-1">{action.desc}</p>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
